import type { SearchMembersFn } from '@/features/iam'
import { computed, action } from 'mobx'
import { task } from 'mobx-task'
import { type Job, PayoutSplit } from '@/features/jobs/Job'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { PayoutSplitAllocatorViewModel } from '@/features/jobs/details/components/PayoutSplitAllocator/PayoutSplitAllocatorViewModel'
import { Id } from '@/infra/id'
import { type JobStore, type AllocationInput } from '@/features/jobs/JobStore'
import type { AlertFactory } from '@/features/shell'

/**
 * How the job's current payout split came to be.
 */
export type SplitPayoutDialogSituation =
  | { type: 'Manual'; allocatedByName: string; allocatedAt: Date }
  | { type: 'Auto'; adjustedAt: Date }
  | { type: 'Default' }

/**
 * Dialog for defining the payout split.
 */
export class SplitPayoutDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * View model for split allocations.
   */
  readonly allocatorVm

  constructor(
    private readonly _job: Job,
    private readonly _findMembers: SearchMembersFn,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
  ) {
    this.allocatorVm = new PayoutSplitAllocatorViewModel(
      Id.assert(this._job.workspace.id),
      () => this._job.providerCutAmount,
      this._findMembers,
    )
  }

  /**
   * The existing split.
   */
  @computed
  get existingSplit() {
    return this._job.payoutSplit ?? PayoutSplit.empty()
  }

  /**
   * How the existing split came to be.
   */
  @computed
  get situation(): SplitPayoutDialogSituation {
    const situation = this.existingSplit.situation
    switch (situation.type) {
      case 'Manual':
        return {
          type: 'Manual',
          allocatedByName: situation.allocatedBy.displayName,
          allocatedAt: situation.allocatedAt,
        }
      case 'Auto':
        return { type: 'Auto', adjustedAt: situation.adjustedAt }
      case 'Default':
        return { type: 'Default' }
    }
  }

  /**
   * Shows the modal.
   */
  @action.bound
  show() {
    this._resetAllocations()
    this.modal.show()
  }

  /**
   * Split the payout according to the defined allocations.
   */
  splitPayout = task.resolved(async () => {
    if (!this.allocatorVm.validate()) {
      return
    }

    const allocations = this.allocatorVm.getAllocations()
    await this._jobStore.splitPayout(this._job, allocations)

    this.modal.close()
    this._newAlert()
      .saying('Payout saved!', 'The new payout split was saved successfully.')
      .withState('success')
      .autoDismiss()
      .show()
  })

  /**
   * Starts the allocator from the job's existing split.
   */
  private _resetAllocations() {
    this.allocatorVm.reset()

    const allocations: AllocationInput[] = this.existingSplit.allocations.map(
      (allocation) => ({
        user: allocation.user,
        allocationAmount: allocation.amount,
        authorizedPartialPayoutAmount: allocation.authorizedPartialPayoutAmount,
      }),
    )
    this.allocatorVm.setAllocations(allocations)
  }
}
